import { Array, pipe, Struct } from "effect";
import { cn } from "../../utils/class-names";
import { MarketHeaderMarket } from "./types";
import { getAnalyticsMarketLink, getAssets } from "@silo/utils";
import { IsolatedRiskPill } from "./isolated-risk";
import { Pill } from "../pill";
import { OraclePill } from "../oracle/oracle-pill";
import { isNotNullable } from "effect/Predicate";
import { match } from "ts-pattern";
import {
  ADDRESS_ZERO,
  DEFAULT_PROTOCOL_KEY,
  isFeatureProtocolInstance,
  LINKS,
  SKELETON_TOKEN_NAME,
  SKELETON_TOKEN_SYMBOL,
} from "@silo/types";
import { ReactNode } from "react";
import { ExternalLink } from "./external-link";
import { ContangoPill } from "./contango-pill";
import {
  AtRiskOfLiquidationChip,
  DeprecatedChip,
  PausedChip,
  UnstableOracleChip,
} from "../alert-chip";
import { SkeletonWrapper } from "../skeleton";

export interface MarketHeaderProps {
  className?: string;
  market: MarketHeaderMarket;
  isContangoEnabled: boolean;
  contangoImage: ReactNode;
  isDeprecated: boolean;
  hasUnstableOracle: boolean;
  isPaused: boolean;
  isAtRiskOfLiquidation: boolean;
}

export function MarketHeader({
  className,
  market,
  isContangoEnabled,
  contangoImage,
  isDeprecated,
  hasUnstableOracle,
  isPaused,
  isAtRiskOfLiquidation,
}: MarketHeaderProps): JSX.Element {
  const {
    protocolKey,
    baseAsset,
    bridgeAssets,
    marketAddress,
    marketName,
    marketSymbol,
    rawMarketSymbol,
  } = market;

  const hasChip =
    isDeprecated || hasUnstableOracle || isPaused || isAtRiskOfLiquidation;

  const assets = getAssets(market);
  const [firstBridgeAsset, secondBridgeAsset] = bridgeAssets;

  const symbols = pipe(assets, Array.map(Struct.get("symbol")));

  const bridgeSymbols = pipe(
    bridgeAssets,
    Array.flatMapNullable((asset) => asset),
    Array.map(Struct.get("symbol"))
  );

  const isSameOracle = pipe(
    assets,
    Array.every((asset) => asset.oracleKey === baseAsset.oracleKey)
  );

  const isSameBridgeOracle =
    isNotNullable(secondBridgeAsset) &&
    firstBridgeAsset.oracleKey === secondBridgeAsset.oracleKey;

  return (
    <Root className={className}>
      <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-5">
        <Heading symbols={symbols} />

        {hasChip && (
          <div className="flex items-center gap-2.5 flex-wrap">
            {isDeprecated && <DeprecatedChip />}

            {hasUnstableOracle && <UnstableOracleChip />}

            {isPaused && <PausedChip />}

            {isAtRiskOfLiquidation && <AtRiskOfLiquidationChip />}
          </div>
        )}
      </div>

      <PillContainer>
        <PillRowContainer>
          <IsolatedRiskPill protocolKey={protocolKey} />

          <OraclePill
            oracleKey={baseAsset.oracleKey}
            oracleAssets={isSameOracle ? "all" : "base"}
            symbols={[baseAsset.symbol]}
            marketAddress={marketAddress}
            marketSymbol={marketSymbol}
            marketName={marketName}
            rawMarketSymbol={rawMarketSymbol}
            protocolKey={protocolKey}
          />

          {match({ isSameOracle, isSameBridgeOracle })
            // scenario: all assets in the market have the same oracle
            .with({ isSameOracle: true }, () => (
              <OraclePill
                oracleKey={firstBridgeAsset.oracleKey}
                oracleAssets="all"
                symbols={bridgeSymbols}
                marketAddress={marketAddress}
                marketSymbol={marketSymbol}
                marketName={marketName}
                rawMarketSymbol={rawMarketSymbol}
                protocolKey={protocolKey}
              />
            ))
            // scenario: there are multiple bridge assets, and they have the same oracle
            .with({ isSameBridgeOracle: true }, () => (
              <OraclePill
                oracleKey={firstBridgeAsset.oracleKey}
                oracleAssets="allBridge"
                symbols={bridgeSymbols}
                marketAddress={marketAddress}
                marketSymbol={marketSymbol}
                marketName={marketName}
                rawMarketSymbol={rawMarketSymbol}
                protocolKey={protocolKey}
              />
            ))
            // any other scenario
            .otherwise(() => (
              <>
                <OraclePill
                  oracleKey={firstBridgeAsset.oracleKey}
                  oracleAssets="other"
                  symbols={[firstBridgeAsset.symbol]}
                  marketAddress={marketAddress}
                  marketSymbol={marketSymbol}
                  marketName={marketName}
                  rawMarketSymbol={rawMarketSymbol}
                  protocolKey={protocolKey}
                />

                {isNotNullable(secondBridgeAsset) && (
                  <OraclePill
                    oracleKey={secondBridgeAsset.oracleKey}
                    oracleAssets="other"
                    symbols={[secondBridgeAsset.symbol]}
                    marketAddress={marketAddress}
                    marketSymbol={marketSymbol}
                    marketName={marketName}
                    rawMarketSymbol={rawMarketSymbol}
                    protocolKey={protocolKey}
                  />
                )}
              </>
            ))}

          {isFeatureProtocolInstance("analytics")(protocolKey) && (
            <Pill>
              Market analytics
              <ExternalLink
                href={getAnalyticsMarketLink(rawMarketSymbol, protocolKey)}
              />
            </Pill>
          )}
        </PillRowContainer>

        <PillRowContainer>
          {isContangoEnabled && <ContangoPill contangoImage={contangoImage} />}

          <StrategyLinkPill />

          <DiscordLinkPill />
        </PillRowContainer>
      </PillContainer>
    </Root>
  );
}

interface PulseMarketHeaderProps {
  className?: string;
}

export function PulseMarketHeader({
  className,
}: PulseMarketHeaderProps): JSX.Element {
  return (
    <Root className={cn("animate-pulse", className)}>
      <SkeletonWrapper>
        <Heading
          symbols={[
            SKELETON_TOKEN_SYMBOL,
            SKELETON_TOKEN_SYMBOL,
            SKELETON_TOKEN_SYMBOL,
          ]}
        />
      </SkeletonWrapper>

      <PillContainer>
        <PillRowContainer>
          <SkeletonWrapper>
            <IsolatedRiskPill protocolKey={DEFAULT_PROTOCOL_KEY} />
          </SkeletonWrapper>

          <SkeletonWrapper>
            <OraclePill
              oracleKey="chainlink"
              oracleAssets="all"
              symbols={[SKELETON_TOKEN_SYMBOL]}
              marketAddress={ADDRESS_ZERO}
              marketSymbol={SKELETON_TOKEN_SYMBOL}
              marketName={SKELETON_TOKEN_NAME}
              rawMarketSymbol={SKELETON_TOKEN_SYMBOL}
              protocolKey={DEFAULT_PROTOCOL_KEY}
            />
          </SkeletonWrapper>

          <SkeletonWrapper>
            <OraclePill
              oracleKey="chainlink"
              oracleAssets="all"
              symbols={[SKELETON_TOKEN_SYMBOL, SKELETON_TOKEN_SYMBOL]}
              marketAddress={ADDRESS_ZERO}
              marketSymbol={SKELETON_TOKEN_SYMBOL}
              marketName={SKELETON_TOKEN_NAME}
              rawMarketSymbol={SKELETON_TOKEN_SYMBOL}
              protocolKey={DEFAULT_PROTOCOL_KEY}
            />
          </SkeletonWrapper>
        </PillRowContainer>

        <PillRowContainer>
          <SkeletonWrapper>
            <StrategyLinkPill />
          </SkeletonWrapper>

          <SkeletonWrapper>
            <DiscordLinkPill />
          </SkeletonWrapper>
        </PillRowContainer>
      </PillContainer>
    </Root>
  );
}

interface RootProps {
  className?: string;
  children?: ReactNode;
}

function Root({ className, children }: RootProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-10 items-start", className)}>
      {children}
    </div>
  );
}

interface HeadingProps {
  className?: string;
  symbols: string[];
}

function Heading({ className, symbols }: HeadingProps): JSX.Element {
  return (
    <h1
      className={cn("text-lg font-medium text-white sm:text-2xl", className)}
      data-cy="silo-title"
    >
      {symbols.join(", ")} market
    </h1>
  );
}

interface PillContainerProps {
  className?: string;
  children?: ReactNode;
}

function PillContainer({
  className,
  children,
}: PillContainerProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-5 md:gap-2.5", className)}>
      {children}
    </div>
  );
}

interface PillRowContainerProps {
  className?: string;
  children?: ReactNode;
}

function PillRowContainer({
  className,
  children,
}: PillRowContainerProps): JSX.Element {
  return (
    <div
      className={cn("flex flex-col md:flex-row items-start gap-2.5", className)}
    >
      {children}
    </div>
  );
}

function StrategyLinkPill(): JSX.Element {
  return (
    <Pill>
      Latest leverage strategies{" "}
      <ExternalLink href={LINKS.SILOPEDIA.BORROWING_STRATEGIES} />
    </Pill>
  );
}

function DiscordLinkPill(): JSX.Element {
  return (
    <Pill>
      For help, join our Discord <ExternalLink href={LINKS.DISCORD} />
    </Pill>
  );
}
